import yup from "../../../../../../utils/yup-extended";
import {ProductGenInfoFormFieldsType} from "../../../../../../components/offering-form/products/gen-info/types";
import {ProductSettingsFormFieldsType} from "../../../../../../components/offering-form/products/settings/types";
import {ProductPricingFormFieldsType} from "../../../../../../components/offering-form/products/pricing/types";
import {pwywCurrencyRowSchema} from "@/features/pricing";
import {IS_PWYW_ENABLED} from "../../../../../../services/config";
import {purchaseLimitValidationSchemaFields} from "../../../../../../components/offering-form/general/settings/validation-schema";

export const hasOptions = ["radioButton", "checkBox", "dropDown"];

/**
 * A currency row under Free or Single Fee — unchanged from before Pay What You Want
 * existed, deliberately: shipping the third model must not move the other two.
 */
const singleFeeCurrencyRowSchema = yup.object().shape({
  callPriceCurrency: yup.string().trim().required("Currency is required"),
  callPrice: yup
    .number()
    .required("Price is required")
    .min(1, "Can't be less than 1")
    .default(1)
    .typeError("Must be a number"),
});

/** With the flag off, Pay What You Want is not a Pricing Model a coach can submit. */
const selectablePriceTypes = IS_PWYW_ENABLED
  ? ["paid", "free", "pwyw"]
  : ["paid", "free"];

export const createProductBasicFormStepFormValidationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Product name is required")
    .typeError("Must be a valid string"),
  description: yup
    .string()
    .trim()
    .required("Product description is required")
    .typeError("Must be a valid string"),
  // coverImage: yup
  //   .string()
  //   .trim()
  //   .required("Cover image is required")
  //   .typeError("Must be a valid string"),
  priceType: yup.string().required().oneOf(selectablePriceTypes, "Select a cost"),
  currencies: yup
    .array()
    .when("priceType", (priceType: string, schema) => {
      if (IS_PWYW_ENABLED && priceType === "pwyw") {
        return schema.of(pwywCurrencyRowSchema);
      }

      // Free keeps one blank row so the coach has a way back to a priced model (see
      // `transitionOfferingFormCurrencyRows`). That row is never rendered under Free, so
      // holding it to a Single Fee price would block the save with an error the coach can
      // neither see nor clear.
      if (priceType === "free") {
        return schema;
      }

      return schema.of(singleFeeCurrencyRowSchema);
    })
    .when("priceType", {
      is: "paid",
      then: (schema) => schema.min(1, "Add at least one price for paid products"),
      otherwise: (schema) => schema,
    }),
  hideFromProfile: yup.bool().default(false),
  requestCustomerPhoneNumber: yup.bool().default(false),
  ...purchaseLimitValidationSchemaFields,
  isUrlRedirect: yup.bool().default(false),
  redirectAfterPurchase: yup
    .mixed<"none" | "telegram" | "custom-url">()
    .oneOf(["none", "telegram", "custom-url"])
    .default("none"),
  telegramGroupId: yup.string().when("redirectAfterPurchase", {
    is: "telegram",
    then: (schema) =>
      schema.required("Telegram group is required").test({
        name: "telegram-group",
        message: "Telegram group is required",
        test: (value) => value !== "none",
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  url_redirect: yup
    .string()
    .nullable()
    .when("isUrlRedirect", {
      is: (isUrlRedirect: boolean) => isUrlRedirect,
      then: (schema) => schema.required("Redirect URL is required").urlWithEmoji().trim(),
      otherwise: (schema) => schema.notRequired(),
    }),

  // variants: yup
  //   .array()
  //   .of(
  //     yup.object().shape({
  //       name: yup.string().trim().required("Variant name is required"),
  //       options: yup
  //         .array()
  //         .of(
  //           yup.object().shape({
  //             type: yup
  //               .string()
  //               .oneOf(["paid", "free"])
  //               .required("Option type is required"),
  //             title: yup.string().trim().required("Option title is required"),
  //             // price: yup.number().when("type", {
  //             //   is: "paid",
  //             //   then: (schema) =>
  //             //     schema
  //             //       .required("Price is required for paid options")
  //             //       .min(1, "Price must be at least 1"),
  //             //   otherwise: (schema) => schema.notRequired(),
  //             // }),
  //             currencies: yup.array().of(
  //               yup.object().shape({
  //                 callPriceCurrency: yup.string().trim().required("Currency is required"),
  //                 callPrice: yup.number().when("$type", {
  //                   is: "paid",
  //                   then: (schema) =>
  //                     schema
  //                       .required("Price is required for paid options")
  //                       .min(1, "Price must be at least 1"),
  //                   otherwise: (schema) => schema.notRequired(),
  //                 }),
  //               })
  //             ),
  //           })
  //         )
  //         .min(1, "At least one option is required"),
  //     })
  //   )
  //   .default([]),
});

export type CreateProductBasicFormStepFormValuesYup = yup.InferType<
  typeof createProductBasicFormStepFormValidationSchema
>;

export type CreateProductBasicFormStepFormValues = ProductGenInfoFormFieldsType &
  ProductSettingsFormFieldsType &
  ProductPricingFormFieldsType;
